import {
  createAction,
  createEntityAdapter,
  createSlice,
  EntityId,
  PayloadAction,
} from "@reduxjs/toolkit";

import { RootState } from "../root-state";

// Simple user interface for authentication
interface AuthUser {
  id: string;
  uid: string;
  email: string | null;
  displayName: string | null;
  emailVerified: boolean;
  photoURL: string | null;
  isAnonymous: boolean;
  metadata?: {
    creationTime?: string;
    lastSignInTime?: string;
  };
}

const authAdapter = createEntityAdapter<AuthUser>();

const initialState = authAdapter.getInitialState({
  isLoading: false,
  user: null as AuthUser | null,
});

export type AuthState = typeof initialState;

const slice = createSlice({
  name: "auth" as const,
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setUser: (state, action: PayloadAction<AuthUser | null>) => {
      state.user = action.payload;
    },
    clearUser: (state) => {
      state.user = null;
    },
    updateUserProfile: (state, action: PayloadAction<{ displayName?: string; photoURL?: string }>) => {
      if (state.user) {
        if (action.payload.displayName !== undefined) {
          state.user.displayName = action.payload.displayName;
        }
        if (action.payload.photoURL !== undefined) {
          state.user.photoURL = action.payload.photoURL;
        }
      }
    },
  },
});

const authAdapterSelectors = authAdapter.getSelectors(
  (state: RootState) => state.auth
);

namespace sliceSelectors {
  export const isLoading = (state: RootState) => state.auth.isLoading;
  export const user = (state: RootState) => state.auth.user;
  export const isAuthenticated = (state: RootState) => !!state.auth.user;
  export const userId = (state: RootState) => state.auth.user?.uid;
}

export const authSlice = {
  ...slice,
  selectors: {
    ...authAdapterSelectors,
    ...sliceSelectors,
  },
};

export type { AuthUser };
